import { ApiError, api } from '../api/client.js';
import { type WalkInState, modeBasePrice, modeService, walkInEndAt } from '../stores/walkIn.js';

/**
 * HM-074 — Walk-in cascade.
 *
 * Sequenza:
 *  1. POST /bookings
 *  2. POST /payments
 *  3. POST /fiscal-receipts
 *  4. POST /access-codes
 *  5. POST /messaging/send
 *
 * Ogni step propaga errori al wizard store (setCascade.error). Lo step UI
 * mostra "loading" sull'attivo. ErrorRecovery (HM-075) gestisce recovery.
 *
 * HM-353 cluster 16.1.6 hotfix: la cascade ora rispetta la scelta
 * dinamica fatta dall'utente in Step 1 (cluster 16.1):
 * - durata booking = dynamicPricing.selectedHours (se selectedMode='hours'),
 *   altrimenti modeDurationMs(mode) legacy come fallback.
 * - totale = dynamicPricing.priceCalc.total se popolato dal preview-calc,
 *   altrimenti modeBasePrice(mode) legacy. Senza questo fix il backend
 *   riceveva sempre 4h/8h/14h fissi e prezzi 75/130/180 anche quando
 *   l'utente aveva scelto 3h/5h/etc., causando false positive
 *   booking_invalid_range (oltre a totali sbagliati).
 *
 * Anonymous flow (HM-350): se isAnonymous=true, il guestName diventa
 * l'anonymousId pattern "Walk-in #YYYY-MM-DD-NNN" (visibile in dashboard
 * + alloggiati). guest.fullName e' vuoto quindi non e' usato.
 */
export type WalkInCascadeWizard = Pick<
  WalkInState,
  | 'room'
  | 'mode'
  | 'guest'
  | 'payment'
  | 'cascade'
  | 'setCascade'
  | 'dynamicPricing'
  | 'isAnonymous'
  | 'anonymousId'
> & { setStep?: (s: WalkInState['step']) => void };

export type WalkInCascadeDeps = {
  /** Override fetch per test (defaults a `api`). */
  apiFn?: typeof api;
  /** Now per test deterministici. */
  now?: () => Date;
};

export async function runWalkInCascade(
  wizard: WalkInCascadeWizard,
  deps: WalkInCascadeDeps = {},
): Promise<void> {
  const {
    room,
    mode,
    guest,
    payment,
    cascade,
    setCascade,
    dynamicPricing,
    isAnonymous,
    anonymousId,
  } = wizard;
  if (!room) return;
  if (cascade.running) return;
  const apiFn = deps.apiFn ?? api;
  const now = deps.now?.() ?? new Date();
  const start = now;

  // HM-353 cluster 16.1.6: usa dynamicPricing quando l'utente ha scelto
  // una durata custom in Step 1; fallback a legacy mode altrimenti.
  // Regola 2026-07-23: pernottamento = uscita entro le 12:00 (walkInEndAt).
  // QA sweep 2026-07-22: logica estratta in walkInDurationMs, condivisa
  // con StepRecap (il recap mostrava +4h fissi ignorando selectedHours).
  const end = walkInEndAt(now, mode, dynamicPricing);
  const total = dynamicPricing?.priceCalc?.total ?? modeBasePrice(mode);
  // Service: dynamicPricing.selectedMode 'hours' -> day_use; 'overnight' -> overnight.
  const service =
    dynamicPricing && dynamicPricing.selectedMode === 'overnight' ? 'overnight' : modeService(mode);
  const guestName = isAnonymous && anonymousId ? anonymousId : guest.fullName || 'Walk-in';

  // HM-355 cluster 16.1.7 graceful degradation: warnings raccolti per
  // step 2-7. Solo step 1 (booking) hard-fail aborta. Tutti gli altri
  // proseguono cascade + UI mostra banner warning ma camera passa
  // OCCUPATA via check_in side-effect rooms.status='occupied'.
  const warnings: { stage: string; message: string }[] = [];

  setCascade({ running: true, error: null, currentStep: 'booking', warnings: [] });

  // 1. Booking — HARD required (skip se gia' creato per retry).
  let bookingId = cascade.bookingId;
  if (!bookingId) {
    try {
      const r = await apiFn<{ booking: { id: string } }>('/bookings', {
        method: 'POST',
        body: JSON.stringify({
          roomId: room.id,
          service,
          startAt: start.toISOString(),
          endAt: end.toISOString(),
          guestName,
          guestPhone: guest.phone ?? null,
          totalAmount: total.toFixed(2),
          adults: 1,
          children: 0,
        }),
      });
      bookingId = r.booking.id;
      setCascade({ bookingId, currentStep: 'payment' });
    } catch (err) {
      setCascade({
        running: false,
        error: { stage: 'booking', message: errMsg(err) },
      });
      return;
    }
  } else {
    setCascade({ currentStep: 'payment' });
  }

  // 2-5: graceful steps. Helper inline per pattern try/catch + warning.
  async function softStep(
    stage: WalkInState['cascade']['currentStep'],
    nextStage: WalkInState['cascade']['currentStep'],
    fn: () => Promise<unknown>,
  ): Promise<unknown> {
    try {
      const r = await fn();
      setCascade({ currentStep: nextStage });
      return r;
    } catch (err) {
      warnings.push({ stage: String(stage), message: errMsg(err) });
      setCascade({ currentStep: nextStage, warnings: [...warnings] });
      return null;
    }
  }

  // 2+3. Payment + Fiscal receipt.
  // Revisione pagamenti 2026-07-22: con payment='later' ("Paga all'uscita")
  // gli step vengono SALTATI puliti — nessuna chiamata a /payments ne'
  // /fiscal-receipts, nessun warning finto. L'incasso e lo scontrino
  // unificato avvengono al checkout; la prenotazione resta paid_amount=0
  // → badge "DA INCASSARE" in dashboard.
  if (payment === 'later') {
    setCascade({ currentStep: 'access_code' });
  } else {
    // ONDATA 1 fix 2: key idempotenza STABILE per booking — un retry
    // (ErrorRecovery, doppio submit) NON riaddebita: il backend risponde
    // 200 replayed col payment gia' incassato.
    await softStep('payment', 'fiscal', () =>
      apiFn('/payments', {
        method: 'POST',
        body: JSON.stringify({
          bookingId,
          method: payment,
          amount: total.toFixed(2),
          idempotencyKey: `${bookingId}:entry`,
        }),
      }),
    );

    // ONDATA 1 fix 1d/5: metodo REALE sullo scontrino (prima il backend
    // defaultava CARD anche per i contanti).
    await softStep('fiscal', 'access_code', () =>
      apiFn('/fiscal-receipts', {
        method: 'POST',
        body: JSON.stringify({
          bookingId,
          items: [{ description: `Walk-in ${room.code} ${mode}`, amount: total.toFixed(2) }],
          vatRate: 10,
          paymentMethod: payment === 'cash' ? 'CASH' : 'CARD',
        }),
      }),
    );
  }

  // 4. Access code — HM-571: backend ritorna {accessCode: null, skipped: true}
  // per tenant a chiavi fisiche (no KNX). softStep lo conta come success.
  const acRes = (await softStep('access_code', 'messaging', () =>
    apiFn<{ accessCode: { code: string } | null; skipped?: boolean }>('/access-codes', {
      method: 'POST',
      body: JSON.stringify({ bookingId }),
    }),
  )) as { accessCode?: { code: string } | null; skipped?: boolean } | null;
  if (acRes?.accessCode?.code) {
    setCascade({ accessCode: acRes.accessCode.code });
  }

  // 5. Messaging — best-effort
  await softStep('messaging', 'confirm', () =>
    apiFn('/messaging/send', {
      method: 'POST',
      body: JSON.stringify({
        bookingId,
        template: 'booking_confirmation',
        to: guest.phone ?? null,
      }),
    }),
  );

  // 6. Confirm booking (pending -> confirmed). HM-355: route nuovo.
  // body '{}' richiesto perche' api() setta Content-Type: application/json
  // e Fastify rifiuta empty body con FST_ERR_CTP_EMPTY_JSON_BODY (400).
  await softStep('confirm', 'check_in', () =>
    apiFn(`/bookings/${bookingId}/confirm`, { method: 'POST', body: '{}' }),
  );

  // 7. Check-in (confirmed -> checked_in + rooms.status='occupied').
  await softStep('check_in', 'done', () =>
    apiFn(`/bookings/${bookingId}/check-in`, { method: 'POST', body: '{}' }),
  );

  // Done — riflette warnings nel currentStep finale.
  setCascade({
    currentStep: warnings.length > 0 ? 'done_with_warnings' : 'done',
    running: false,
    warnings: [...warnings],
  });
}

export async function rollbackBooking(
  wizard: WalkInCascadeWizard,
  deps: WalkInCascadeDeps = {},
): Promise<void> {
  const apiFn = deps.apiFn ?? api;
  const id = wizard.cascade.bookingId;
  if (!id) return;
  try {
    await apiFn(`/bookings/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason: 'walk-in cascade aborted' }),
    });
  } catch {
    // ignore — best-effort
  }
}

function errMsg(err: unknown): string {
  if (err instanceof ApiError) {
    return `${err.status}: ${err.code ?? err.message}`;
  }
  if (err instanceof Error) return err.message;
  return 'Errore sconosciuto';
}
